import { FormControl, Stack, TextField, Typography } from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
  useWatch
} from 'react-hook-form'

import { PasswordTextField } from 'components/common/PasswordTextField'
import {
  AuthenticationFormValues,
  getExternalIntegrationForObservabilityTool,
  setObservabilityToolAndDomain
} from 'components/status-checks/authentication-form-utils'
import { StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { ObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useTranslation } from 'hooks/use-translation'
import { TeamExternalIntegrationAuthenticationStatus } from 'types/api/status-checks/status-check'
import { IntegrationType } from 'utils/team-external-integrations/team-external-integration-types'

export const AWSServiceAccountAuthentication: React.FC = () => {
  const { trans } = useTranslation()
  const [awsAccountId, setAwsAccountId] = useState<string>('')
  const [awsAccessKeyID, setAwsAccessKeyID] = useState<string>('')
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState<string>('')

  const { getAvailableExternalIntegrations } =
    useStatusCheckExternalIntegrationsContext()
  const { isEdit, isDrawerFlow } = useContext(
    ObservabilityToolAuthenticationContext
  )
  const form = useFormContext<
    StatusCheckFormValues | AuthenticationFormValues
  >()
  const { control, formState, setValue, trigger } = form

  const [selectedObservabilityTool, externalIntegration] = useWatch({
    control,
    name: ['observabilityTool', 'externalIntegration']
  })

  const externalIntegrations = getAvailableExternalIntegrations(false)

  const handleInputAWSDomain = async (
    value: string,
    field: ControllerRenderProps<
      StatusCheckFormValues | AuthenticationFormValues,
      'domain.name'
    >
  ) => {
    // Prevents inadvertently setting other fields to a value from an existing authenticated account
    if (!value) {
      return
    }

    const selectedObservabilityToolWithNewDomain = {
      ...selectedObservabilityTool,
      selectedDomain: value
    }

    const externalIntegration = getExternalIntegrationForObservabilityTool(
      selectedObservabilityToolWithNewDomain,
      externalIntegrations,
      !isEdit
    )

    setObservabilityToolAndDomain(
      selectedObservabilityToolWithNewDomain,
      {
        name: value,
        integrationType: IntegrationType.AWS
      },
      externalIntegration,
      form
    )

    // Re-validate domain whenever it changes
    await trigger('domain')

    field.onChange(value)
  }

  // Puts values into state if we are editing an existing AWS authentication
  useEffect(() => {
    if (
      isEdit &&
      externalIntegration?.isConfigured &&
      externalIntegration?.lastAuthenticationStatus ===
        TeamExternalIntegrationAuthenticationStatus.Authenticated
    ) {
      setValue('domain', {
        name: externalIntegration?.domain,
        integrationType: IntegrationType.AWS
      })
      setValue(
        'awsAccessKeyID',
        externalIntegration.integrationSpecificValues?.AWS_ACCESS_KEY_ID || ''
      )
      setValue(
        'awsSecretAccessKey',
        externalIntegration.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY ||
          ''
      )
      setAwsAccountId(externalIntegration.domain)
      setAwsAccessKeyID(
        externalIntegration.integrationSpecificValues?.AWS_ACCESS_KEY_ID || ''
      )
      setAwsSecretAccessKey(
        externalIntegration.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY ||
          ''
      )
    }
  }, [
    externalIntegration?.domain,
    externalIntegration?.integrationSpecificValues?.AWS_ACCESS_KEY_ID,
    externalIntegration?.integrationSpecificValues?.AWS_SECRET_ACCESS_KEY,
    externalIntegration?.isConfigured,
    externalIntegration?.lastAuthenticationStatus,
    isEdit,
    setValue
  ])

  return (
    <Stack ml={2} spacing={3}>
      {!isEdit && (
        <FormControl>
          <Typography mb={2}>
            {trans(
              'forms.save_status_check.select_observability_tool_and_domain.observability_tool_aws_account_id.prompt'
            )}
          </Typography>
          <Controller
            control={control}
            defaultValue={awsAccountId}
            name="domain.name"
            render={({ field }) => (
              <TextField
                {...field}
                data-testid="aws-account-id-input"
                disabled={isEdit}
                error={Boolean(formState.errors?.domain?.name)}
                helperText={formState.errors?.domain?.name?.message}
                label={trans(
                  'forms.save_status_checks.authentication.aws.aws_account_id.text_field.label'
                )}
                value={awsAccountId}
                variant="outlined"
                onBlur={(e) => handleInputAWSDomain(e.target.value, field)}
                onChange={(e) => {
                  setAwsAccountId(e.target.value)
                }}
              />
            )}
          />
        </FormControl>
      )}
      <FormControl>
        <Typography mb={2}>
          {trans('forms.save_status_checks.authentication.aws.prompt')}
        </Typography>
        <Stack
          direction={isDrawerFlow ? 'column' : 'row'}
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Controller
            control={control}
            defaultValue={awsAccessKeyID}
            name="awsAccessKeyID"
            render={({ field }) => (
              <PasswordTextField
                {...field}
                autoComplete="off"
                data-testid="aws-access-key-id-input"
                error={Boolean(formState.errors?.awsAccessKeyID)}
                helperText={formState.errors?.awsAccessKeyID?.message}
                label={trans(
                  'forms.save_status_checks.authentication.aws.aws_access_key_id.text_field.label'
                )}
                value={awsAccessKeyID}
                variant="outlined"
                onBlur={(e) => {
                  setAwsAccessKeyID(e.target.value)
                  field.onChange(e)
                }}
                onChange={(e) => {
                  setAwsAccessKeyID(e.target.value)
                  field.onChange(e)
                }}
              />
            )}
          />
          <Controller
            control={control}
            defaultValue={awsSecretAccessKey}
            name="awsSecretAccessKey"
            render={({ field }) => (
              <PasswordTextField
                {...field}
                autoComplete="off"
                data-testid="aws-secret-access-key-input"
                error={Boolean(formState.errors?.awsSecretAccessKey)}
                helperText={formState.errors?.awsSecretAccessKey?.message}
                label={trans(
                  'forms.save_status_checks.authentication.aws.aws_secret_access_key.text_field.label'
                )}
                value={awsSecretAccessKey}
                variant="outlined"
                onBlur={(e) => {
                  setAwsSecretAccessKey(e.target.value)
                  field.onChange(e)
                }}
                onChange={(e) => {
                  setAwsSecretAccessKey(e.target.value)
                }}
              />
            )}
          />
        </Stack>
      </FormControl>
    </Stack>
  )
}
